import { useCallback, useState } from "react";
import { useLocation, useNavigate } from "react-router";
import { usePageLoader } from "../../../../common/contexts/page_content_context";
import { useDependencyInjection } from "../../../../config/di/dependency_injection_context";
import { processError } from "../../../../common/utils/base_error";
import { usePageStartBootstrap } from "../../../../common/hooks/use_page_start_bootstrap";
import { useForm } from "react-hook-form";
import { useChangeHistory } from "../../../../common/hooks/use_change_history";
import { zodResolver } from "@hookform/resolvers/zod";
import { LogTipoTabela } from "../../../../common/utils/log_tabela_tipo";
import { defaultManutencaoForm, ManutencaoForm, ManutencaoSchema } from "../schemas/Manutencao/manutencao";
import { ManutencaoModel } from "../../domain/models/manutencao_model";
import { DropDown } from "../../../../common/domain/models/dropdown";

const convertStatusStringToNumber = (status: string) => {
  switch (status) {
    case 'E':
      return 0;
    case 'A':
      return 1;
    case 'R':
      return 2;
    case 'F':
      return 3;
    default:
      return 0;
  }
}

const convertStatusNumberToString = (status: number) => {
  switch (status) {
    case 0:
      return 'E';
    case 1:
      return 'A';
    case 2:
      return 'R';
    case 3:
      return 'F';
    default:
      return 'E';
  }
}

export const useManutencaoSolicitacaoCadastroViewModel = () => {
  const {
    commons: {
      getAllLogAlteracaoUseCase,
      getAllLogAlteracaoByParentUseCase,
    },
    controlePatrimonio: {
      dropDownMaquinaEquipUseCase,
      getManutencaoByIdUseCase,
      updateManutencaoUseCase,
      createManutencaoUseCase,
    },

  } = useDependencyInjection();

  const location = useLocation();
  const navigate = useNavigate();

  const { pushErrorNotification, pushInfoNotification } = usePageLoader();

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const [errorMessage, setErrorMessage] = useState("");
  const [selectedManutencao, setSelectedManutencao] = useState<ManutencaoModel>();
  const [ddlMaquinaEquip, setDdlMaquinaEquip] = useState<DropDown[]>([]);

  const [visualizar, setVisualizar] = useState<boolean>(false);

  const hookForm = useForm<ManutencaoForm>({
    reValidateMode: "onSubmit",
    mode: "onSubmit",
    defaultValues: defaultManutencaoForm,
    resolver: zodResolver(ManutencaoSchema),
  });

  const changeHistory = useChangeHistory({
    getAllLogAlteracaoUseCase,
    getAllLogAlteracaoByParentUseCase,
    onSetError: (e) => pushErrorNotification("Erro", [e]),
  });

  //#region CARREGAMENTO 

  const loadDropdownMaquinaEquip = useCallback(async () => {
    try {
      setLoading(true);
      const result = await dropDownMaquinaEquipUseCase.dropDownMaquinaEquip();
      setDdlMaquinaEquip(result.map(item => ({ id: item.idMaquinaEquip, title: item.descricao })));
    } catch (e: unknown) {
      pushErrorNotification("Erro", processError(e, "Falha ao carregar máquinas/equipamentos!"));
      setDdlMaquinaEquip([]);
    } finally {
      setLoading(false);
    }
  }, [
    dropDownMaquinaEquipUseCase,
    pushErrorNotification
  ]);


  const loadManutencaoById = useCallback(
    async (id: number) => {
      setLoading(true);
      try {
        const result = await getManutencaoByIdUseCase.execute(id);
        setSelectedManutencao(result);

        hookForm.setValue("idManutencao", result.idManutencao);
        hookForm.setValue("status", convertStatusStringToNumber(result.status));
        hookForm.setValue("nroManutencao", result.nroManutencao);
        hookForm.setValue("idMaquinaEquip", result.idMaquinaEquip);
        hookForm.setValue("idCentroCusto", result.idCentroCusto);
        hookForm.setValue("idFuncionarioSolicitante", result.idFuncionarioSolicitante);
        hookForm.setValue("idFuncionarioAprovacao", result.idFuncionarioAprovacao);
        hookForm.setValue("idFornecedorResponsavel", result.idFornecedorResponsavel);
        hookForm.setValue("valorOrcamento", result.valorOrcamento);
        hookForm.setValue("dataPrevConclusao", result.dataPrevConclusao);
        hookForm.setValue("dataConclusao", result.dataConclusao);
        hookForm.setValue("dataAprovacao", result.dataAprovacao);
        hookForm.setValue("descricao", result.descricao ?? undefined);
        hookForm.setValue("observacao", result.observacao ?? undefined);
        hookForm.setValue("dataSolicitacao", result.dataSolicitacao ? new Date(result.dataSolicitacao) : undefined);
        hookForm.setValue("idFuncionarioResponsavel", result.idFuncionarioResponsavel);
        hookForm.setValue("tipoManutencao", result.tipoManutencao);
        hookForm.setValue("motivoReprovacao", result.motivoReprovacao ?? undefined);

      } catch (e: unknown) {
        setErrorMessage(processError(e, "Falha ao carregar revisão")[0]);
      } finally {
        setLoading(false);
      }
    },
    [
      hookForm,
      getManutencaoByIdUseCase,
    ],
  );

  //#endregion



  //#region PAGE START

  usePageStartBootstrap(async () => {
    try {
      const params = new URLSearchParams(location.search);
      const locationId = params.get("id");

      const visualizarParam = params.get("visualizar");
      setVisualizar(visualizarParam === "true");
      const id = locationId ?? undefined;

      await loadDropdownMaquinaEquip();
      if (id) {
        await loadManutencaoById(Number(locationId));
      }

    } catch (e: unknown) {
      setErrorMessage(processError(e, "Falha ao buscar dados das páginas!")[0]);

    }
  });


  //#endregion



  //#region CREATE/UPDATE


  const handleSubmitManutencao = useCallback(async (form: ManutencaoForm) => {
    try {

      setSaving(true);

      if (selectedManutencao) {
        await updateManutencaoUseCase.execute(
          form.idManutencao ?? 0,
          convertStatusNumberToString(form.status),
          form.idCentroCusto ?? undefined,
          form.idFuncionarioAprovacao ?? undefined,
          form.dataPrevConclusao ?? undefined,
          form.dataConclusao ?? undefined,
          form.dataAprovacao ?? undefined,
          form.observacao ?? undefined,
          form.idMaquinaEquip ?? undefined,
          form.idFuncionarioSolicitante ?? undefined,
          form.idFornecedorResponsavel ?? undefined,
          form.valorOrcamento ?? undefined,
          form.descricao ?? undefined,
          form.dataSolicitacao ?? undefined,
          form.idFuncionarioResponsavel ?? undefined,
          form.tipoManutencao ?? undefined,
          form.motivoReprovacao ?? undefined
        );

        await loadManutencaoById(selectedManutencao.idManutencao);
        pushInfoNotification("Sucesso", "Dados salvos com sucesso", { isAutoFinish: true });
      } else {
        const idManutencao = await createManutencaoUseCase.execute(
          form.nroManutencao ?? 0,
          form.idManutencao ?? 0,
          convertStatusNumberToString(form.status),
          form.idCentroCusto ?? undefined,
          form.idMaquinaEquip ?? undefined,
          form.idFuncionarioSolicitante ?? undefined,
          form.idFuncionarioAprovacao ?? undefined,
          form.idFornecedorResponsavel ?? undefined,
          form.valorOrcamento ?? undefined,
          form.dataPrevConclusao ?? undefined,
          form.dataConclusao ?? undefined,
          form.dataAprovacao ?? undefined,
          form.descricao ?? undefined,
          form.observacao ?? undefined,
          form.dataSolicitacao ?? undefined
        );

        setVisualizar(true);
        await loadManutencaoById(idManutencao);
        pushInfoNotification("Sucesso", "Manutenção criada com sucesso", { isAutoFinish: true });
      }
    } catch (e: unknown) {
      setErrorMessage(processError(e, "Falha ao salvar revisão!")[0]);
    } finally {
      setSaving(false);
    }
  }, [
    selectedManutencao,
    updateManutencaoUseCase,
    createManutencaoUseCase,
    loadManutencaoById,
    pushInfoNotification,
  ]);

  //#endregion



  //#region CABEÇALHO

  const handlePageBack = useCallback(() => {
    navigate('/Manutencao_lista');
  }, [navigate]);


  const handleReloadPage = () => {
    hookForm.reset();
    const params = new URLSearchParams(location.search);
    const locationId = params.get("id");
    if (locationId) loadManutencaoById(Number(locationId));

  };

  //#endregion

  //#region  Historico

  const handleOpenChangeHistory = useCallback(
    (idTabela: number | null, tipoTabela: LogTipoTabela | null) => {
      if (idTabela && tipoTabela) {
        changeHistory.handleOpenChangeHistory(idTabela, tipoTabela, false);
      } else {
        if (selectedManutencao) {
          changeHistory.handleOpenChangeHistory(selectedManutencao.idManutencao, LogTipoTabela.MANUTENCAO, true);
        }
      }
    },
    [changeHistory, selectedManutencao],
  );

  //#endregion

  return {
    loading,
    selectedManutencao,
    hookForm,
    errorMessage,
    saving,
    changeHistory,
    ddlMaquinaEquip: ddlMaquinaEquip ?? [],
    visualizar,

    handleOpenChangeHistory,
    handleSubmitManutencao: hookForm.handleSubmit(handleSubmitManutencao),
    handlePageBack,
    handleReloadPage,
  };
};
